import { useState, useEffect } from 'react';

const faqs = [
  {
    question: 'What is the minimum order quantity?',
    answer: 'Minimum order quantity varies by product category. For toner cartridges, it\'s typically 10 units. For lamination pouches, it\'s 1 box (100 sheets). Contact us for specific MOQ details.',
  },
  {
    question: 'How do I get bulk pricing?',
    answer: 'Simply submit a quote request through our website or WhatsApp with your requirements. Our team will respond within 24 hours with competitive bulk pricing based on your order volume.',
  },
  {
    question: 'Do you provide GST invoices?',
    answer: 'Yes, we provide proper GST invoices for all B2B transactions. We are a registered business and ensure complete tax compliance for your records.',
  },
  {
    question: 'What are the dispatch timelines?',
    answer: 'For in-stock items, we dispatch within 24-48 hours of payment confirmation. Delivery typically takes 3-7 days depending on your location across India.',
  },
  {
    question: 'Which cities do you serve?',
    answer: 'We serve all major cities across India including Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad, Pune, Ahmedabad, and many more. We have pan-India logistics coverage.',
  },
  {
    question: 'What payment modes are accepted?',
    answer: 'We accept bank transfers (NEFT/RTGS/IMPS), UPI payments, and for established dealers, we also offer credit terms after verification.',
  },
  {
    question: 'How do I become a dealer?',
    answer: 'Fill out our dealer application form with your business details. Our team will verify your information and contact you within 2-3 business days to discuss partnership terms.',
  },
  {
    question: 'Can I get product samples?',
    answer: 'Yes, we can provide samples for evaluation. Sample charges may apply and will be adjusted against your first bulk order. Contact us to request samples.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gi2merchants.com';

  useEffect(() => {
    // Add FAQPage Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };

    let schemaScript = document.querySelector('script[data-schema="faq"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'faq');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(faqSchema);

    return () => {
      const script = document.querySelector('script[data-schema="faq"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 lg:py-20 bg-gray-50">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-teal-600 transition-all duration-300"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <span className="text-lg font-bold text-gray-900 pr-8">
                  {faq.question}
                </span>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`${
                      openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                    } text-2xl text-teal-700 transition-transform duration-300`}
                  ></i>
                </div>
              </button>

              {/* Answer */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 pt-2">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
